import { Box, Button, Divider, Group, LoadingOverlay, Modal, Stack } from '@mantine/core';

import { ORGANIZATION_MODAL_MODE } from '@/types/churches';
import type {
  Church,
  ChurchCreateRequest,
  ChurchUpdateRequest,
} from '@/types/churches';
import type { MyParishBulletinUpdateRequest, MyParishView } from '@/types/myParish';
import { SaveErrorBox } from '@/components/SaveErrorBox/SaveErrorBox';
import { useOrganizationForm } from './useOrganizationForm';
import { OrganizationBasicFields } from './OrganizationBasicFields';
import { OrganizationContactFields } from './OrganizationContactFields';
import { OrganizationBulletinFields } from './OrganizationBulletinFields';
import { OrganizationClusterSection } from './OrganizationClusterSection';
import styles from './EditOrganizationModal.module.sass';

type EditOrganizationModalBaseProps = {
  opened: boolean;
  onClose: () => void;
};

type EditOrganizationModalEditProps = EditOrganizationModalBaseProps & {
  mode?: ORGANIZATION_MODAL_MODE.EDIT;
  organization: Church;
  onSave: (updatedOrganization: ChurchUpdateRequest) => Promise<void> | void;
  myParishData?: MyParishView | null;
  onSaveBulletin?: (data: MyParishBulletinUpdateRequest) => Promise<void> | void;
};

type EditOrganizationModalCreateProps = EditOrganizationModalBaseProps & {
  mode: ORGANIZATION_MODAL_MODE.CREATE;
  organization?: null;
  onCreate: (data: ChurchCreateRequest) => Promise<void> | void;
};

type EditOrganizationModalProps = EditOrganizationModalEditProps | EditOrganizationModalCreateProps;

const LAYOUT = {
  MODAL_SIZE: 'xl',
  LOADING_OVERLAY_Z_INDEX: 400,
};

export const EditOrganizationModal = (props: EditOrganizationModalProps) => {
  const { opened, onClose } = props;
  const isCreateMode = props.mode === ORGANIZATION_MODAL_MODE.CREATE;

  const form = useOrganizationForm({
    organization: isCreateMode ? null : props.organization,
    opened,
    mode: props.mode ?? ORGANIZATION_MODAL_MODE.EDIT,
    onSave: isCreateMode ? undefined : props.onSave,
    onCreate: isCreateMode ? props.onCreate : undefined,
    onClose,
    myParishData: isCreateMode ? undefined : props.myParishData,
    onSaveBulletin: isCreateMode ? undefined : props.onSaveBulletin,
  });

  const isClusterEditable = isCreateMode || Boolean(props.organization?.isClusterEditable);

  return (
    <Modal
      opened={opened}
      onClose={form.handleCancel}
      size={LAYOUT.MODAL_SIZE}
      title={isCreateMode ? 'Create Organization' : 'Organization Details'}
      classNames={{
        header: styles.modalHeader,
        title: styles.modalTitle,
        close: styles.closeButton,
      }}
    >
      <Box pos="relative">
        <LoadingOverlay visible={form.isSaving} zIndex={LAYOUT.LOADING_OVERLAY_Z_INDEX} />
        <Group gap="xl" align="flex-start" pt="sm">
          <Stack gap="md" style={{ flex: 1 }}>
            <OrganizationBasicFields
              formData={form.formData}
              statesData={form.statesData}
              stateSearch={form.stateSearch}
              setStateSearch={form.setStateSearch}
              stateCombobox={form.stateCombobox}
              isDiocese={form.isDiocese}
              dioceseSearch={form.dioceseSearch}
              latitudeError={form.latitudeError}
              longitudeError={form.longitudeError}
              onInputChange={form.handleInputChange}
              onCountryChange={form.handleCountryChange}
              onCoordinateChange={form.handleCoordinateChange}
            />
            <OrganizationContactFields
              formData={form.formData}
              timezoneSearch={form.timezoneSearch}
              setTimezoneSearch={form.setTimezoneSearch}
              timezoneCombobox={form.timezoneCombobox}
              onInputChange={form.handleInputChange}
              onPhoneChange={form.handlePhoneChange}
            />
          </Stack>
          <Stack gap="md" style={{ flex: 1 }}>
            <OrganizationBulletinFields
              discoverMassId={form.formData.discoverMassId}
              bulletinType={form.bulletinType}
              bulletinNumber={form.bulletinNumber}
              bulletinNumberError={form.bulletinNumberError}
              showBulletinFields={!form.isCreateMode}
              onDiscoverMassIdChange={(value) => form.handleInputChange('discoverMassId', value)}
              onBulletinTypeChange={form.handleBulletinTypeChange}
              onBulletinNumberChange={form.handleBulletinNumberChange}
            />
            {!form.isDiocese && isClusterEditable && (
              <OrganizationClusterSection
                formData={form.formData}
                isClusterEditable={isClusterEditable}
                clusterSearch={form.clusterSearch}
                onInputChange={form.handleInputChange}
                onRemoveClusterOrganization={form.handleRemoveClusterOrganization}
                onClusterSelection={form.handleClusterSelection}
                onClusterProductsChange={form.handleClusterProductsChange}
              />
            )}
          </Stack>
        </Group>
        <Divider my="lg" />
        <SaveErrorBox error={form.saveError} />
        <Group justify="flex-end" gap="md">
          <Button variant="default" onClick={form.handleCancel} disabled={form.isSaving}>
            Cancel
          </Button>
          <Button onClick={form.handleSave} disabled={!form.isFormValid() || form.isSaving}>
            {isCreateMode ? 'Create' : 'Save'}
          </Button>
        </Group>
      </Box>
    </Modal>
  );
};
